import { useCallback, useRef, useState } from 'react'

import { useAssistantServices } from '../AssistantServices'

export const STOP_NOTICE = 'Stopped here. The answer may still be running on the server.'

/**
 * Stop: the client stream stops at once, then the run is cancelled on the server.
 * The run is durable and does not depend on the connection, so ending the stream
 * first loses nothing, and the answer stops on screen while the server call (up
 * to a few seconds) is still going. When the cancel call fails, the user is told
 * the run may still be going. A cancel that finds nothing to stop (the send had
 * not started, or the run had ended) is not a problem.
 */
export function useStopAnswer({ threadId, stop }: { threadId: string; stop: () => void }) {
  const { threads } = useAssistantServices()
  const [isStopping, setIsStopping] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const inFlight = useRef(false)

  const stopAnswer = useCallback(async () => {
    if (inFlight.current) return

    inFlight.current = true
    setIsStopping(true)
    setNotice(null)

    stop()

    try {
      await threads.cancel(threadId)
    } catch {
      setNotice(STOP_NOTICE)
    } finally {
      inFlight.current = false
      setIsStopping(false)
    }
  }, [threads, threadId, stop])

  const clearNotice = useCallback(() => setNotice(null), [])

  return { stopAnswer, isStopping, notice, clearNotice }
}
